import { ReactNode } from "react"
import { cn } from "@/utils/helpers"

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost"
  size?: "sm" | "md" | "lg"
  isLoading?: boolean
  children: ReactNode
}

export function Button({
  variant = "primary",
  size = "md",
  isLoading = false,
  disabled = false,
  className,
  children,
  ...props
}: ButtonProps) {
  const baseStyles =
    "inline-flex items-center justify-center gap-2 rounded-full font-light transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#999189]/30 dark:focus-visible:ring-white/20 disabled:opacity-50 disabled:cursor-not-allowed"

  const variants = {
    primary:
      "bg-[#1a1818] text-white shadow-[0_8px_20px_-12px_rgba(26,24,24,0.30)] hover:shadow-[0_10px_28px_-12px_rgba(26,24,24,0.35)] active:translate-y-0 active:shadow-[0_4px_12px_-8px_rgba(26,24,24,0.20)] dark:bg-[linear-gradient(130deg,#2f6bbf,#2f94c3)] dark:text-white dark:shadow-[0_8px_20px_-12px_rgba(10,32,70,0.30)]",
    secondary: "bg-[#fcfcfa] text-[#1a1818] border border-[#e5ddd6] hover:bg-[#f5f3f1] hover:shadow-[0_2px_8px_rgba(26,24,24,0.06)] dark:border-white/20 dark:hover:bg-white/10",
    outline: "border border-[#e5ddd6] text-[#1a1818] bg-[#fcfcfa] hover:bg-[#faf8f5] dark:border-white/24 dark:bg-white/10 dark:hover:bg-white/16",
    ghost: "text-[#999189] hover:text-[#1a1818] hover:bg-[#f5f3f1]/50 dark:hover:bg-white/10",
  }

  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base font-light",
  }

  return (
    <button
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <span
            aria-hidden="true"
            className="inline-block w-4 h-4 mr-2 border-2 border-current border-r-transparent rounded-full animate-spin"
          />
          Loading...
        </>
      ) : (
        children
      )}
    </button>
  )
}
